import {
  Actions,
  ADD_PROJECT_PRESTATIONS,
  UPDATE_PROPOSAL,
  ADD_PRESTATION_PROPOSALS,
  ADD_PROJECTS,
  START_LOADING_PROJECTS,
  START_LOADING_PRESTATIONS,
  START_LOADING_PROPOSALS,
  UPDATE_PROJECT_STATS_AND_ACTIONS
} from "./actions";
import {
  State,
  initialState,
  Proposal,
  Prestation,
  Project,
  ResponseTypes
} from "./state";
import {
  getProposal,
  getPrestation,
  getProject
} from "src/redux/projects/selectors";

function mergeProjects(state, action) {
  const mergedProjects = [
    ...action.projects.filter(
      p => !state.projects.find(newP => p.id === newP.id)
    ),
    ...state.projects
  ];

  const projects = mergedProjects.sort((a, b) => {
    if (a.name.toLowerCase() < b.name.toLowerCase()) {
      return -1;
    }
    if (a.name.toLowerCase() > b.name.toLowerCase()) {
      return 1;
    }
    return 0;
  });

  return Object.assign({}, state, { projects, isLoadingProjects: false });
}

function updateProject(state, action) {
  const project = getProject(state, action) as Project;

  if (action.responseType === ResponseTypes.Pending) {
    return state;
  }
  const newPrestationsAction = project.prestationsAction.slice();
  const index = project.prestationsAction.findIndex(
    a => a.uuid === action.proposalUuid
  );

  newPrestationsAction.splice(index, 1);

  const newProject = Object.assign({}, project, {
    prestationsAction: newPrestationsAction,
    isLoadingPrestations: false
  });
  const projectIndex = state.projects.findIndex(r => r.id === action.projectId);
  const newProjects = state.projects.slice();
  newProjects.splice(projectIndex, 1, newProject);

  return Object.assign({}, state, {
    projects: newProjects,
    isLoadingProjects: false
  });
}

function addPrestationsToProject(state: State, action) {
  const project = getProject(state, action) as Project;

  const newPrestations = project.prestations.slice();
  for (const prestation of action.prestations) {
    const index = newPrestations.findIndex(p => p.id === prestation.id);
    if (index > -1) {
      newPrestations.splice(index, 1, prestation);
    } else {
      newPrestations.push(prestation);
    }
  }
  const newPrestationsWithSort = newPrestations.sort((a, b) => {
    if (a.name.toLowerCase() < b.name.toLowerCase()) {
      return -1;
    }
    if (a.name.toLowerCase() > b.name.toLowerCase()) {
      return 1;
    }
    return 0;
  });
  const newProject = Object.assign({}, project, {
    prestations: newPrestationsWithSort,
    isLoadingPrestations: false
  });

  const projectIndex = state.projects.findIndex(r => r.id === action.projectId);
  const newProjects = state.projects.slice();
  newProjects.splice(projectIndex, 1, newProject);

  return Object.assign({}, state, { projects: newProjects });
}

function setPrestationsAsLoading(state: State, action) {
  const project = getProject(state, action) as Project;

  const newProject = Object.assign({}, project, {
    isLoadingPrestations: true
  });

  const projectIndex = state.projects.findIndex(r => r.id === action.projectId);
  const newProjects = state.projects.slice();
  newProjects.splice(projectIndex, 1, newProject);

  return Object.assign({}, state, { projects: newProjects });
}

function addOrUpdateProposal(state, action, proposal) {
  const prestation = getPrestation(state, action) as Prestation;
  const project = getProject(state, action) as Project;

  const newProposals = prestation.proposals.slice();
  const proposalIndex = newProposals.findIndex(p => p.id === proposal.id);
  if (proposalIndex > -1) {
    newProposals.splice(proposalIndex, 1, proposal);
  } else {
    newProposals.push(proposal);
  }

  const newPrestation = Object.assign({}, prestation, {
    proposals: newProposals
  });
  newPrestation.proposals.sort((a, b) => a.date.diff(b.date, "second"));
  const prestationIndex = project.prestations.findIndex(
    r => r.id === action.prestationId
  );

  const newPrestations = project.prestations.slice();
  newPrestations.splice(prestationIndex, 1, newPrestation);

  const newProject = Object.assign({}, project, {
    prestations: newPrestations
  });

  const projectIndex = state.projects.findIndex(r => r.id === action.projectId);
  const newProjects = state.projects.slice();
  newProjects.splice(projectIndex, 1, newProject);

  return Object.assign({}, state, { projects: newProjects });
}

function addProposalsToPrestation(state, action) {
  const prestation = getPrestation(state, action) as Prestation;
  const project = getProject(state, action) as Project;

  const newPrestation = Object.assign({}, prestation, {
    proposals: [
      ...prestation.proposals.filter(
        p => !action.proposals.find(newP => newP.id === p.id)
      ),
      ...action.proposals
    ],
    isLoadingProposals: false
  });
  newPrestation.proposals.sort((a, b) => a.date.diff(b.date, "second"));
  const prestationIndex = project.prestations.findIndex(
    r => r.id === action.prestationId
  );

  const newPrestations = project.prestations.slice();
  newPrestations.splice(prestationIndex, 1, newPrestation);

  const newProject = Object.assign({}, project, {
    prestations: newPrestations
  });

  const projectIndex = state.projects.findIndex(r => r.id === action.projectId);
  const newProjects = state.projects.slice();
  newProjects.splice(projectIndex, 1, newProject);

  return Object.assign({}, state, { projects: newProjects });
}

function setProposalsAsLoading(state, action) {
  const prestation = getPrestation(state, action) as Prestation;
  const project = getProject(state, action) as Project;

  const newPrestation = Object.assign({}, prestation, {
    isLoadingProposals: true
  });

  const prestationIndex = project.prestations.findIndex(
    r => r.id === action.prestationId
  );

  const newPrestations = project.prestations.slice();
  newPrestations.splice(prestationIndex, 1, newPrestation);

  const newProject = Object.assign({}, project, {
    prestations: newPrestations
  });

  const projectIndex = state.projects.findIndex(r => r.id === action.projectId);
  const newProjects = state.projects.slice();
  newProjects.splice(projectIndex, 1, newProject);

  return Object.assign({}, state, { projects: newProjects });
}

export function reducer(state: State = initialState, action: Actions): State {
  switch (action.type) {
    case ADD_PROJECTS:
      return mergeProjects(state, action);
    case UPDATE_PROJECT_STATS_AND_ACTIONS:
      return updateProject(state, action);
    case ADD_PROJECT_PRESTATIONS:
      return addPrestationsToProject(state, action);
    case ADD_PRESTATION_PROPOSALS:
      return addProposalsToPrestation(state, action);
    case UPDATE_PROPOSAL:
      const proposal = getProposal(state, action) as Proposal;
      const newProposal = Object.assign({}, proposal, action.proposal);
      return addOrUpdateProposal(state, action, newProposal);
    case START_LOADING_PROJECTS:
      return Object.assign({}, state, { isLoadingProjects: true });
    case START_LOADING_PRESTATIONS:
      return setPrestationsAsLoading(state, action);
    case START_LOADING_PROPOSALS:
      return setProposalsAsLoading(state, action);
    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/redux/projects/reducer.ts